import './App.css'
import { useEffect, useState } from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { BrowserRouter as Router } from 'react-router-dom'
import AppRoutes from './Routes.js'
import { initializeApi } from './Assets/API/apiservices'

const theme = createTheme({
  palette: {
    mode: 'dark', // Makes contrast better for dark maroon
    background: {
      mainBackground: '#494798',
      primaryBackground: '#f15215',
      secondaryBackground: '#FFA726',
      paper: '#E6D8B6',
      drawerBackground: '#5E2D0F',
      whiteBackground: '#fff',
      footerBackground: 'rgb(104, 112, 185)',
    },
    primary: {
      main: '#f15215',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F5F5F5', // Soft white
      dark: '#121212',
    },
    text: {
      main: '#494798',
      orange: '#f15215',
      primary: '#000000',
      secondary: '#e09f3e',
      paragraph: '#757e91',
      accent: '#FF6F61',
      muted: '#757e91',
      inverse: '#ffffff',
      danger: '#e63946',
      success: '#4caf50',
      warning: '#ffa500',
      info: '#00bcd4',
    },
  },

  typography: {
    fontFamily: ['Montserrat', 'sans-serif'].join(','),
    h1: {
      fontSize: '2.75rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      color: '#B0B8C1',
    },
    subtitle2: {
      fontSize: '0.875rem',
      color: '#9CA3AF',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontSize: '1rem',
      fontWeight: '600',
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#9CA3AF',
    },
    overline: {
      fontSize: '0.625rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#1C1C1E',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          padding: '24px',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#6E1F1F',
          color: '#ffffff',
          boxshadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          borderRadius: '10px',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#5E2D0F',
          color: '#ffffff',
          width: 360,
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 700,
          color: '#FFA726',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            color: '#ffffff',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#FF6F61',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4FC3F7',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#FFA726',
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          padding: '16px',
          borderRadius: '12px',
          boxshadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,167,38,0.25)',
            '& .MuiListItemText-primary': {
              color: '#FFA726',
            },
          },
        },
      },
    },

    MuiSnackbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FF6F61',
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: '#2E2E38',
          color: '#ffffff',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#6E1F1F',
          '& .MuiTableCell-head': {
            color: '#FFA726',
            fontWeight: 700,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#FFA726',
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#3B3B47',
        },
        indicator: {
          backgroundColor: '#FF6F61',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          color: '#ffffff',
          '&.Mui-selected': {
            color: '#FFA726',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#FF6F61',
          color: '#ffffff',
          fontWeight: 600,
        },
        outlined: {
          borderColor: '#FF6F61',
          color: '#FF6F61',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          color: '#ffffff',
          padding: '24px',
          borderRadius: '16px',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#FFA726',
          fontWeight: 700,
          fontSize: '1.25rem',
        },
      },
    },
  },
})
function App() {
  const [apiReady, setApiReady] = useState(false)

  useEffect(() => {
    const init = async () => {
      await initializeApi()
      setApiReady(true)
    }
    init()
  }, [])

  if (!apiReady) {
    return (
      <div style={{ color: '#fff', textAlign: 'center', paddingTop: '50px' }}>
        Loading API...
      </div>
    )
  }

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div
          style={{
            backgroundColor: theme.palette.background.whiteBackground,
            minHeight: '100vh',
            color: theme.palette.text.paragraph,
          }}
        >
          <AppRoutes />
        </div>
      </ThemeProvider>
    </Router>
  )
}

export default App
